import {
  Accordion,
  Button,
  Checkbox,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import {
  DesignPackagePruneSlotsModal_DataQuery,
  useDesignPackagePruneSlotsModal_DataQuery,
  useDesignPackagePruneSlotsModal_SaveDraftMutation,
} from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackagePruneSlotsModal.generated";
import { queryResult } from "src/utils";

type DesignPackagePruneSlotsModalProps = {
  designPackageId: string;
  versionId: string;
};

export function DesignPackagePruneSlotsModal(props: DesignPackagePruneSlotsModalProps) {
  const { designPackageId, versionId } = props;
  const { triggerNotice } = useSnackbar();
  const { closeModal } = useModal();
  const [saveDraft] = useDesignPackagePruneSlotsModal_SaveDraftMutation();
  const [selectedTlis, setSelectedTlis] = useState<string[]>([]);
  // Query for unused slots
  const query = useDesignPackagePruneSlotsModal_DataQuery({ variables: { designPackageId, versionId } });

  return queryResult(query, ({ takeoffLineItemsPage }) => {
    // there's no filter for this in TakeoffLineItemFilter, so we'll do it in memory
    const takeoffLineItems = takeoffLineItemsPage.items.filter((tli) => tli.active);
    return (
      <>
        <ModalHeader>Prune Unused Slots</ModalHeader>
        <ModalBody>
          <DesignPackagePruneSlotsModalBody
            selectedTlis={selectedTlis}
            setSelectedTlis={setSelectedTlis}
            takeoffLineItems={takeoffLineItems}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="quaternary" label="Cancel" onClick={closeModal} />
          <Button
            variant="primary"
            label="Prune Slots"
            disabled={takeoffLineItems.isEmpty}
            onClick={async () => {
              // Deactivate takeoff line items for selected slots
              await saveDraft({
                variables: { readyPlanId: designPackageId, input: selectedTlis.map((id) => ({ id, active: false })) },
                refetchQueries: ["DesignPackageSlots"],
              });
              triggerNotice({ message: `${selectedTlis.length} slots have been deactivated`, icon: "success" });
              closeModal();
            }}
          />
        </ModalFooter>
      </>
    );
  });
}

function DesignPackagePruneSlotsModalBody(props: {
  takeoffLineItems: DesignPackagePruneSlotsModal_DataQuery["takeoffLineItemsPage"]["items"];
  selectedTlis: string[];
  setSelectedTlis: (v: string[]) => void;
}) {
  const tid = useTestIds({});
  const { takeoffLineItems, selectedTlis, setSelectedTlis } = props;

  const groupedTlis = useMemo(
    () =>
      takeoffLineItems
        // Group by Location (prefer Nook, then Room, then ID--should move to the backend DesignPackageSlot identity)
        .groupByObject((tli) => {
          const { id, feature, room, nonScopeLocation } = tli.slot.location;
          return feature ? [room?.name, feature.name].compact().join(" > ") : nonScopeLocation.name;
        })
        .map(
          ([group, tlis]) =>
            // Subgroup by Cost Code
            [group, tlis.groupByObject((tli) => tli.slot.item.costCode.name).sortBy(([subgroup]) => subgroup)] as const,
        )
        .sortBy(([group]) => group),
    [takeoffLineItems],
  );

  const toggleTli = (tliId: string) =>
    setSelectedTlis(selectedTlis.includes(tliId) ? selectedTlis.without(tliId) : [...selectedTlis, tliId]);

  return (
    <>
      <div>Select unused slots to prune from the latest draft of this design package</div>
      {takeoffLineItems.isEmpty ? (
        <div>No unused slots</div>
      ) : (
        <div css={Css.my2.$}>
          {groupedTlis.map(([group, subgroups]) => (
            <Accordion
              key={group}
              title={group}
              defaultExpanded
              xss={Css.fwb.tal.p1.truncate.$ as any}
              topBorder={false}
              omitPadding
              {...tid.group}
            >
              <div css={Css.pl2.$}>
                {subgroups.map(([subgroup, tlis]) => (
                  <Accordion
                    key={subgroup}
                    title={subgroup}
                    defaultExpanded
                    xss={Css.tal.p1.truncate.$ as any}
                    topBorder={false}
                    {...tid.subgroup}
                  >
                    {tlis.map((tli, i) => (
                      <div key={tli.id} css={Css.df.aic.ml1.px1.pyPx(4).br4.if(tlis.length > i + 1).mb1.$} {...tid.tli}>
                        <div css={Css.mya.$}>
                          <Checkbox
                            label={tli.name}
                            selected={selectedTlis.includes(tli.id)}
                            onChange={() => toggleTli(tli.id)}
                            checkboxOnly // hide the default label and implement our own so we have control of the style
                          />
                        </div>
                        <div css={Css.pl1.onHover.cursorPointer.$} onClick={() => toggleTli(tli.id)}>
                          {tli.name}
                        </div>
                      </div>
                    ))}
                  </Accordion>
                ))}
              </div>
            </Accordion>
          ))}
        </div>
      )}
    </>
  );
}
